import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { WagmiProvider } from "wagmi";
import { DynamicContextProvider, mergeNetworks } from "@dynamic-labs/sdk-react-core";
import { DynamicWagmiConnector } from "@dynamic-labs/wagmi-connector";
import { EthereumWalletConnectors } from "@dynamic-labs/ethereum";
import { SolanaWalletConnectors } from "@dynamic-labs/solana";
import { TronWalletConnectors } from "@dynamic-labs/tron";
import type { EvmNetwork } from "@dynamic-labs/types";
import { RouterProvider } from "@tanstack/react-router";
import { wagmiConfig } from "~/components/Wallet/config";
import { VIEM_CHAINS } from "~/lib/chains";
import { router } from "~/router";
import "./styles/globals.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

const dynamicEvmNetworks: EvmNetwork[] = Object.values(VIEM_CHAINS).map((chain) => ({
  key: `evm-${chain.id}`,
  name: chain.name,
  shortName: chain.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
  chain: "EVM" as const,
  chainId: chain.id,
  networkId: chain.id,
  isTestnet: chain.testnet,
  iconUrls: [],
  nativeCurrency: chain.nativeCurrency,
  rpcUrls:
    chain.rpcUrls.default.http.length > 0
      ? [...chain.rpcUrls.default.http]
      : [...(chain.rpcUrls.public?.http ?? [])],
  blockExplorerUrls: chain.blockExplorers?.default?.url ? [chain.blockExplorers.default.url] : [],
}));

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <DynamicContextProvider
      settings={{
        environmentId: "d12a6c75-2dab-474b-b0a9-34809acf0910",
        walletConnectors: [
          EthereumWalletConnectors,
          SolanaWalletConnectors,
          TronWalletConnectors,
        ],
        initialAuthenticationMode: "connect-only",
        overrides: {
          evmNetworks: (dashboardNetworks) => mergeNetworks(dynamicEvmNetworks, dashboardNetworks),
        },
      }}
    >
      <WagmiProvider config={wagmiConfig}>
        <QueryClientProvider client={queryClient}>
          <DynamicWagmiConnector>
            <RouterProvider router={router} />
          </DynamicWagmiConnector>
        </QueryClientProvider>
      </WagmiProvider>
    </DynamicContextProvider>
  </StrictMode>,
);
